// User Types
export interface User {
  uid: string;
  email: string;
  username: string;
  displayName: string;
  photoURL: string;
  bio: string;
  provider: 'email' | 'google' | 'apple';
  isAdmin: boolean;
  isBanned: boolean;
  banReason: string | null;
  bannedAt: number | null;
  bannedBy: string | null;
  banExpiresAt: number | null;
  isFoundingMember?: boolean;
  foundingMemberDate?: number | null;
  tags?: string[]; // Array of tag IDs the user has earned
  favorites?: {
    sports?: string[];
    teams?: string[];
  };
  settings?: UserSettings;
  reputation?: UserReputation;
  stats?: UserStats;
  createdAt: number;
  updatedAt: number;
}

export interface UserSettings {
  notifications?: {
    picks: boolean;
    sweatrooms: boolean;
    follows: boolean;
    tips: boolean;
    trivia: boolean;
  };
  darkMode: boolean;
  tutorialCompleted: boolean;
  privacy?: {
    profilePublic: boolean;
    showActivity: boolean;
  };
}

export interface UserReputation {
  score: number;
  level: number;
  currentStreak: number;
  bestStreak: number;
  lastActiveDate: string;
  badges?: Record<string, boolean>;
  trophies?: Record<string, boolean>;
}

export interface UserStats {
  totalSlips: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  totalTails: number;
  sweatRoomsJoined: number;
  tipsGiven: number;
  tipsReceived: number;
  triviaCorrect: number;
}

// GT (GroundToken) Types
export interface GTWallet {
  balance: number;
  totalEarned: number;
  totalSpent: number;
  totalTippedGiven: number;
  totalTippedReceived: number;
  lastUpdated: number;
  version: number;
}

export interface GTTransaction {
  transactionId: string;
  type: 'purchase' | 'earn_slip' | 'earn_trivia' | 'earn_streak' | 'earn_engagement' | 'earn_tip' | 'spend_tip' | 'spend_store' | 'refund' | 'admin_adjustment' | 'signup_offer';
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  referenceId: string | null;
  referenceType: string | null;
  description: string;
  metadata?: Record<string, any>;
  createdAt: number;
  processedBy: string | null;
}

export interface TokenPackage {
  packageId: string;
  name: string;
  tokens: number;
  bonusTokens: number;
  priceUsdCents: number;
  stripePriceId: string;
  stripeProductId?: string;
  isActive: boolean;
  sortOrder: number;
  description?: string;
  badge?: string;
  createdAt?: number;
  updatedAt?: number;
}

export interface GTPurchase {
  purchaseId: string;
  userId: string;
  stripePaymentIntentId: string;
  stripeCustomerId: string | null;
  packageId: string;
  packageName: string;
  tokensBase: number;
  tokensBonus: number;
  tokensTotal: number;
  amountCents: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  createdAt: number;
  completedAt: number | null;
  failureReason: string | null;
}

// Admin Types
export interface AdminStats {
  totalUsers: number;
  newUsers: number;
  activeUsers: number;
  totalSlips: number;
  newSlips: number;
  verifiedSlips: number;
  totalSweatRooms: number;
  activeSweatRooms: number;
  totalMessages: number;
  gtCirculating: number;
  gtEarnedToday: number;
  gtSpentToday: number;
  gtPurchasedToday: number;
  revenueCentsToday: number;
  triviaParticipation: number;
  storeRedemptions: number;
  pendingReports: number;
  resolvedReports: number;
}

export interface Report {
  reportId: string;
  reportedBy: string;
  reportedByUsername: string;
  reportedUserId: string | null;
  reportedUsername: string | null;
  contentType: 'slip' | 'post' | 'comment' | 'user' | 'message';
  contentId: string;
  contentPreview: string;
  reason: string;
  description: string;
  status: 'pending' | 'reviewing' | 'resolved' | 'dismissed';
  reviewedBy: string | null;
  reviewedByUsername: string | null;
  action: 'none' | 'removed' | 'warned' | 'banned' | null;
  actionNotes: string | null;
  createdAt: number;
  resolvedAt: number | null;
}

export type StoreCategory = 'merch' | 'tickets' | 'profile' | 'giftcard';
export type RedemptionType = 'code' | 'physical' | 'digital';
export type ProfileItemType = 'badge' | 'frame';

export interface StoreItem {
  itemId: string;
  name: string;
  description: string;
  category: StoreCategory;
  subcategory?: string;
  imageURL: string;
  gtPrice: number | null; // null = marketplace item (external purchase)
  marketplaceUrl?: string; // External link for marketplace items
  stock: number | null; // null = unlimited
  stockReserved: number;
  isActive: boolean;
  isFeatured: boolean;
  redemptionType: RedemptionType;
  redemptionInstructions?: string;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
  // Profile item specific fields (for category = 'profile')
  profileItemType?: ProfileItemType; // badge or frame
  profileItemId?: string; // The badge/frame ID to unlock
  profileItemData?: {
    imageUrl?: string; // For frames
    iconName?: string; // For badges (Feather icon name)
    color?: string; // Badge background color
    textColor?: string; // Badge text color
  };
}

export type RedemptionStatus =
  | 'pending'      // Order placed, awaiting admin
  | 'processing'   // Admin working on it
  | 'shipped'      // Physical: shipped
  | 'delivered'    // Physical: delivered
  | 'fulfilled'    // Complete
  | 'cancelled';   // Cancelled

export interface ShippingInfo {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone?: string;
}

export interface StatusChange {
  status: RedemptionStatus;
  timestamp: number;
  changedBy: string;  // admin UID or 'system'
  message?: string;   // Optional message to user
}

export interface StoreRedemption {
  redemptionId: string;
  userId: string;
  username: string;
  email: string;
  itemId: string;
  itemName: string;
  category: StoreCategory;
  redemptionType: RedemptionType;
  gtSpent: number;
  status: RedemptionStatus;
  statusHistory?: StatusChange[];
  fulfillmentCode: string | null;
  shippingInfo?: ShippingInfo;
  trackingNumber?: string;
  trackingCarrier?: string;
  trackingUrl?: string;
  estimatedDelivery?: number;
  userNotes?: string;
  adminNotes?: string;
  cancelReason?: string;
  cancelledBy?: string;
  cancelledAt?: number;
  shippedAt?: number;
  deliveredAt?: number;
  createdAt: number;
  processedAt: number | null;
  fulfilledAt: number | null;
  fulfilledBy: string | null;
  // For profile items - what was unlocked
  profileItemType?: ProfileItemType;
  profileItemId?: string;
  itemImageURL?: string;
}

// Auth Types
export interface AuthState {
  user: User | null;
  firebaseUser: any | null;
  loading: boolean;
  error: string | null;
}

// Profile Tag Types
export interface ProfileTag {
  tagId: string;
  name: string; // Display name e.g. "OG", "Founding Member", "Beta Tester"
  description: string;
  color: string; // Hex color for the tag background
  textColor: string; // Hex color for the tag text
  icon?: string; // Optional icon name
  sortOrder: number;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

// Signup Offer Types
export interface SignupOffer {
  offerId: string;
  name: string;
  description: string;
  gtBonus: number;
  tagId: string | null; // Profile tag awarded to user on signup
  badgeId: string | null;
  badgeImageUrl: string | null;
  discordLink: string | null;
  welcomeTitle: string;
  welcomeMessage: string;
  isActive: boolean;
  startsAt: number;
  expiresAt: number;
  maxRedemptions: number | null; // null = unlimited
  currentRedemptions: number;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
}

export interface SignupOfferRedemption {
  redemptionId: string;
  offerId: string;
  offerName: string;
  userId: string;
  username: string;
  gtAwarded: number;
  tagAwarded: string | null; // Tag ID awarded
  badgeAwarded: string | null;
  redeemedAt: number;
}

// ============================================
// Slip Verification Types
// ============================================

export type SubmissionStatus = 'pending' | 'processing' | 'approved' | 'rejected' | 'flagged';

export interface ProcessingResult {
  success: boolean;
  submissionId?: string;
  status?: string;
  confidence?: number;
  qualityScore?: number;
  error?: string;
  errorCode?: string;
  errorStack?: string;
  issues?: Array<{
    code: string;
    severity: 'error' | 'warning' | 'info';
    title: string;
    message: string;
    suggestion: string;
  }>;
  extraction?: {
    sportsbook: string | null;
    betType: string | null;
    totalOdds: string | null;
    wagerAmount: string | null;
    potentialPayout: string | null;
    picksCount: number;
  };
  userMessage: {
    title: string;
    message: string;
    icon: string;
    color: string;
    canRetry: boolean;
  };
  canProceed?: boolean;
  shouldRetake?: boolean;
  processedAt?: number;
}

export interface SlipSubmission {
  submissionId: string;
  userId: string;
  // Note: username/userPhotoURL fetched from /users/{userId} - optional for backwards compatibility
  username?: string;
  userPhotoURL?: string;
  imageURL: string;
  imageHash?: string;
  status: SubmissionStatus;
  priority: number; // 0=normal, 1=high, 2=urgent
  source: 'app' | 'web' | 'test';

  aiExtraction?: AIExtraction;
  manualReview?: ManualReview;
  duplicateCheck?: DuplicateCheck;
  resolution?: Resolution;
  processingResult?: ProcessingResult;
  processingError?: string;
  qualityAnalysis?: {
    score: number;
    checks: Record<string, { passed: boolean; message: string; value?: number }>;
    issues: Array<{
      code: string;
      severity: 'error' | 'warning' | 'info';
      title: string;
      message: string;
      suggestion?: string;
    }>;
  };

  createdAt: number;
  updatedAt: number;
}

export interface AIExtraction {
  rawText: string;
  confidence: number;
  extractedAt: number;
  sportsbook: string | null;
  betType: string | null;
  totalOdds: string | null;
  wagerAmount: string | null;
  potentialPayout: string | null;
  picks: ExtractedPick[];
  flags: string[];
  error?: string;
}

export interface ExtractedPick {
  event: string;
  selection: string;
  odds: string;
  sport: string | null;
  league: string | null;
  confidence: number;
}

export interface ManualReview {
  reviewedBy: string;
  reviewedByUsername: string;
  reviewedAt: number;
  sportsbook: string;
  betType: string;
  totalOdds: string;
  wagerAmount: string;
  potentialPayout: string;
  picks: ReviewedPick[];
  notes: string;
}

export interface ReviewedPick {
  sport: string;
  league: string;
  event: string;
  selection: string;
  odds: string;
  gameTime: number | null;
}

export interface DuplicateCheck {
  checkedAt: number;
  isDuplicate: boolean;
  similarSubmissions: SimilarSubmission[];
  hashMatches: string[];
}

export interface SimilarSubmission {
  submissionId: string;
  similarity: number;
  userId: string;
  status: string;
}

export interface Resolution {
  decision: 'approved' | 'rejected';
  reason: string | null;
  resolvedBy: string;
  resolvedByUsername: string;
  resolvedAt: number;
  createdSlipId: string | null;
}

export interface VerificationStats {
  totalSubmissions: number;
  pending: number;
  approved: number;
  rejected: number;
  flagged: number;
  duplicatesDetected: number;
  avgProcessingTime: number;
}

// Predefined rejection reasons
export const REJECTION_REASONS = [
  { id: 'duplicate', label: 'Duplicate slip - already submitted' },
  { id: 'unreadable', label: 'Image is unreadable or too blurry' },
  { id: 'incomplete', label: 'Incomplete slip - missing information' },
  { id: 'edited', label: 'Image appears to be edited/modified' },
  { id: 'not_slip', label: 'Not a valid betting slip' },
  { id: 'expired', label: 'Slip has already settled/expired' },
  { id: 'unsupported_book', label: 'Unsupported sportsbook' },
  { id: 'other', label: 'Other (see notes)' }
] as const;

// Supported sportsbooks for detection
export const SUPPORTED_SPORTSBOOKS = [
  'DraftKings',
  'FanDuel',
  'BetMGM',
  'Caesars',
  'PointsBet',
  'BetRivers',
  'Barstool',
  'WynnBET',
  'Bet365',
  'Bovada',
  'BetUS',
  'Other'
] as const;

// Bet types
export const BET_TYPES = [
  'Straight',
  'Parlay',
  'Teaser',
  'Round Robin',
  'Prop',
  'Futures',
  'Live/In-Play',
  'Other'
] as const;

// Sports and leagues
export const SPORTS_LEAGUES = {
  'Football': ['NFL', 'NCAAF', 'CFL', 'XFL'],
  'Basketball': ['NBA', 'NCAAB', 'WNBA', 'EuroLeague'],
  'Baseball': ['MLB', 'NPB', 'KBO'],
  'Hockey': ['NHL', 'AHL', 'KHL'],
  'Soccer': ['EPL', 'La Liga', 'Serie A', 'Bundesliga', 'MLS', 'Champions League'],
  'MMA': ['UFC', 'Bellator', 'PFL', 'ONE'],
  'Boxing': ['Boxing'],
  'Golf': ['PGA', 'LIV', 'LPGA'],
  'Tennis': ['ATP', 'WTA', 'Grand Slam'],
  'Other': ['Other']
} as const;

// ============================================
// User-Friendly Status & Error Messages
// ============================================

export interface StatusMessage {
  title: string;
  message: string;
  icon: 'clock' | 'alert' | 'check' | 'x' | 'camera' | 'refresh';
  color: 'warning' | 'error' | 'success' | 'info' | 'orange';
  canRetry: boolean;
}

export interface QualityIssue {
  code: string;
  severity: 'error' | 'warning' | 'info';
  title: string;
  message: string;
  suggestion: string;
}

// Status messages shown to users based on submission status
export const SUBMISSION_STATUS_MESSAGES: Record<SubmissionStatus, StatusMessage> = {
  pending: {
    title: "Under Review",
    message: "Your slip is being reviewed by our team. This usually takes just a few minutes.",
    icon: "clock",
    color: "warning",
    canRetry: false
  },
  processing: {
    title: "Processing",
    message: "We're analyzing your slip image. Please wait...",
    icon: "refresh",
    color: "info",
    canRetry: false
  },
  flagged: {
    title: "Needs Attention",
    message: "We had some trouble reading your slip. An admin will review it manually, but you may want to resubmit a clearer image.",
    icon: "alert",
    color: "orange",
    canRetry: true
  },
  approved: {
    title: "Approved!",
    message: "Your slip is now live! Others can see it, tail it, and join the SweatRoom.",
    icon: "check",
    color: "success",
    canRetry: false
  },
  rejected: {
    title: "Not Approved",
    message: "Your slip couldn't be verified. See below for details.",
    icon: "x",
    color: "error",
    canRetry: true
  }
};

// Detailed rejection messages with user-friendly explanations
export const REJECTION_MESSAGES: Record<string, StatusMessage & { details: string }> = {
  duplicate: {
    title: "Duplicate Slip",
    message: "This slip has already been submitted.",
    details: "Each betting slip can only be shared once on ParlayGround. If you believe this is an error, please contact support.",
    icon: "x",
    color: "error",
    canRetry: false
  },
  unreadable: {
    title: "Image Too Blurry",
    message: "We couldn't read the text on your slip clearly.",
    details: "Please retake the photo in good lighting. Make sure the camera is focused and hold your phone steady. The entire slip should be sharp and readable.",
    icon: "camera",
    color: "error",
    canRetry: true
  },
  incomplete: {
    title: "Incomplete Image",
    message: "Part of the slip is missing from the image.",
    details: "Please capture the ENTIRE slip including: the sportsbook logo/name at the top, ALL of your picks, the wager amount, and the potential payout at the bottom.",
    icon: "camera",
    color: "error",
    canRetry: true
  },
  edited: {
    title: "Image Appears Modified",
    message: "This image looks like it may have been edited.",
    details: "Only original, unmodified screenshots directly from your sportsbook app are accepted. Please take a fresh screenshot and try again.",
    icon: "x",
    color: "error",
    canRetry: false
  },
  not_slip: {
    title: "Not a Betting Slip",
    message: "This doesn't appear to be a valid betting slip.",
    details: "Please upload a screenshot of an active bet from your sportsbook app. The image should show your picks, odds, and wager amount.",
    icon: "x",
    color: "error",
    canRetry: true
  },
  expired: {
    title: "Slip Already Settled",
    message: "This bet has already been graded (won/lost/pushed).",
    details: "Only active, pending bets can be shared. The slip must show the bet is still open and waiting for the game(s) to complete.",
    icon: "clock",
    color: "error",
    canRetry: false
  },
  unsupported_book: {
    title: "Sportsbook Not Supported",
    message: "We don't currently support this sportsbook.",
    details: "Supported sportsbooks include: DraftKings, FanDuel, BetMGM, Caesars, PointsBet, BetRivers, Bet365, and more. We're always adding new books!",
    icon: "x",
    color: "error",
    canRetry: false
  },
  too_dark: {
    title: "Image Too Dark",
    message: "The image is too dark to read clearly.",
    details: "Please retake the photo in a well-lit area. Natural daylight or bright indoor lighting works best. Avoid shadows falling across the slip.",
    icon: "camera",
    color: "error",
    canRetry: true
  },
  too_bright: {
    title: "Image Overexposed",
    message: "The image is too bright or washed out.",
    details: "There's too much glare or the image is overexposed. Try moving away from direct light sources or windows, and retake the photo.",
    icon: "camera",
    color: "error",
    canRetry: true
  },
  too_small: {
    title: "Image Too Small",
    message: "The image resolution is too low.",
    details: "Please upload a higher quality image. The slip text needs to be large enough to read clearly. Try getting closer to the slip or using your phone's full camera resolution.",
    icon: "camera",
    color: "error",
    canRetry: true
  },
  other: {
    title: "Submission Rejected",
    message: "Your slip couldn't be verified.",
    details: "Please review your image and try again. If you continue having issues, contact our support team for help.",
    icon: "x",
    color: "error",
    canRetry: true
  }
};

// Quality check issue codes and messages
export const QUALITY_ISSUES: Record<string, QualityIssue> = {
  low_confidence: {
    code: 'low_confidence',
    severity: 'warning',
    title: 'Low Recognition Confidence',
    message: 'We had difficulty reading some parts of your slip.',
    suggestion: 'The image may be blurry or poorly lit. Consider retaking in better conditions.'
  },
  unknown_sportsbook: {
    code: 'unknown_sportsbook',
    severity: 'warning',
    title: 'Sportsbook Not Detected',
    message: 'We couldn\'t identify which sportsbook this is from.',
    suggestion: 'Make sure the sportsbook logo or name is visible in the image.'
  },
  no_picks_detected: {
    code: 'no_picks_detected',
    severity: 'warning',
    title: 'No Picks Found',
    message: 'We couldn\'t detect any betting picks in the image.',
    suggestion: 'Ensure all your picks are visible and the text is readable.'
  },
  minimal_text: {
    code: 'minimal_text',
    severity: 'error',
    title: 'Very Little Text Detected',
    message: 'Almost no text could be read from this image.',
    suggestion: 'The image may be too blurry, dark, or the slip may be cut off. Please retake.'
  },
  duplicate_detected: {
    code: 'duplicate_detected',
    severity: 'error',
    title: 'Possible Duplicate',
    message: 'This slip may have already been submitted.',
    suggestion: 'Each slip can only be shared once.'
  },
  image_too_dark: {
    code: 'image_too_dark',
    severity: 'error',
    title: 'Image Too Dark',
    message: 'The image is too dark to read properly.',
    suggestion: 'Retake the photo in a well-lit area with good lighting.'
  },
  image_too_bright: {
    code: 'image_too_bright',
    severity: 'warning',
    title: 'Image Overexposed',
    message: 'The image has too much glare or is washed out.',
    suggestion: 'Move away from direct light and retake without glare.'
  },
  image_blurry: {
    code: 'image_blurry',
    severity: 'error',
    title: 'Image Blurry',
    message: 'The image is out of focus.',
    suggestion: 'Hold your phone steady and tap to focus before taking the photo.'
  },
  resolution_too_low: {
    code: 'resolution_too_low',
    severity: 'error',
    title: 'Resolution Too Low',
    message: 'The image is too small to read clearly.',
    suggestion: 'Use a higher quality camera setting or get closer to the slip.'
  },
  no_odds_found: {
    code: 'no_odds_found',
    severity: 'warning',
    title: 'No Odds Detected',
    message: 'We couldn\'t find any betting odds in the image.',
    suggestion: 'Make sure the odds (like +150 or -110) are visible.'
  },
  high_user_volume: {
    code: 'high_user_volume',
    severity: 'info',
    title: 'Multiple Recent Submissions',
    message: 'You\'ve submitted several slips recently.',
    suggestion: 'This is just a note - your submission is still being processed.'
  }
};

// OG Hub Chat Types
export interface OGHubMessage {
  messageId: string;
  userId: string;
  username: string;
  userPhotoURL?: string;
  content: string;
  type: 'chat' | 'idea' | 'question' | 'feedback';
  reactions?: Record<string, string[]>; // emoji -> userIds
  createdAt: number;
}

// AI Processing result interface (returned from function)
export interface AIProcessingResult {
  success: boolean;
  submissionId: string;
  status: SubmissionStatus;
  confidence: number;
  qualityScore: number;
  issues: QualityIssue[];
  extraction?: {
    sportsbook: string | null;
    betType: string | null;
    totalOdds: string | null;
    wagerAmount: string | null;
    potentialPayout: string | null;
    picksCount: number;
  };
  userMessage: StatusMessage;
  canProceed: boolean;
  shouldRetake: boolean;
}

// ============================================
// Trivia Types
// ============================================

export type TriviaDifficulty = 'easy' | 'medium' | 'hard';

export interface TriviaOption {
  text: string;
  imageUrl?: string;
}

export interface TriviaQuestion {
  questionId: string;
  date: string; // YYYY-MM-DD format, one trivia per day
  question: string;
  questionImageUrl?: string;
  options: TriviaOption[];
  correctIndices: number[]; // Array of correct option indices
  sport?: string;
  league?: string;
  difficulty: TriviaDifficulty;
  gtReward: number;
  streakBonusPerDay: number;
  totalAnswers: number;
  correctAnswers: number;
  optionStats: number[];
  isActive: boolean;
  createdAt: number;
  createdBy: string;
}

export interface TriviaResponse {
  questionId: string;
  userId: string;
  selectedIndices: number[];
  isCorrect: boolean;
  gtEarned: number;
  streakAtTime: number;
  answeredAt: number;
}

export interface UserTriviaStreak {
  currentStreak: number;
  bestStreak: number;
  lastAnsweredDate: string;
  totalCorrect: number;
  totalAnswered: number;
}
